import { createProxySignal, date, DateTimeString, DocumentPrintData, MachineInfo, PrintRequest, requireContext, toast, uuidv4, visitorManagementClient, VisitorManagementBridgeStatus } from 'common';
import { Accessor, createContext, createEffect, createSignal, Setter } from 'solid-js'
import { createStore, SetStoreFunction, Store } from 'solid-js/store';
import { AAMVAInfo, ActiveCheckinInfo, BlockedVisitorMatchDto, DocumentScanResult, SexOffender, VerificationResult, visitorCheckin as api } from 'st-app/api'
import { DocumentReaderContext } from '../st-document-reader/DocumentReaderContext';

export type VerificationStatus = 'verified' | 'dangerous' | 'verifying' | 'error' | 'pending' | 'review'

// The persisted EPC_VM_VISITOR.STATUS code. 'REVW' (screening failed, awaiting staff
// resolution) is distinct from VerificationStatus's client-side 'review' above, which is a
// Warn-tier watch-list hint and is never persisted.
export type VisitorStatus = 'PEND' | 'ALLOW' | 'DENY' | 'REVW'

export type CheckInConfigurationHistoryEntry = {
    visitorType: string;
    reason: string;
    notes: string;
    meetingWith: string;
    meetingWithName: string;
}

// Payload the server pushes into the wizard's bound CheckInJson element when staff
// click [Review] on a row a kiosk flagged during screening. Keys mirror the server
// serialization exactly (K12.VisitorManagement VisitorCheckInCheckOutUI); every value
// arrives as a JSON string, never null.
export type ReviewPayload = {
    mode: 'review';
    vmVisitorGU: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;      // MM/DD/YYYY
    idNumber: string;
    visitorType: string;
    checkInReason: string;
    meetingWith: string;      // staff GU, or '' when the visitor picked nobody
    meetingWithName: string;  // resolved staff formatted name, or '' when meetingWith is '' (or unresolvable)
    portraitBase64: string;
    idImageBase64: string;
}

// ReviewPayload.dateOfBirth is MM/DD/YYYY but AAMVAInfo.dateOfBirth is a DateTimeString,
// so convert. Anything unparseable degrades to date.Empty instead of throwing.
const reviewDobToDateTimeString = (value: string | undefined): DateTimeString => {
    const parts = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((value ?? '').trim());
    if (!parts) return date.Empty;

    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    const year = parseInt(parts[3], 10);

    // Range-check before handing the parts to Date(). Without this, JS silently ROLLS OVER
    // out-of-range components — "13/45/2020" becomes 2021-02-14 — so a garbled DOB would be
    // displayed to staff as a plausible-but-wrong date instead of being shown as absent.
    if (month < 1 || month > 12 || day < 1 || day > 31) return date.Empty;

    const dt = new Date(year, month - 1, day);
    if (isNaN(dt.getTime())) return date.Empty;

    // Catches the in-range-but-nonexistent cases Date() also rolls over (e.g. 02/31).
    if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return date.Empty;

    return date.toJson(dt);
}

/**
 * Outcome of the review-row concurrency pre-check. Three states, not a boolean: a failed
 * CHECK is not the same thing as a row someone else already resolved, and they need
 * different messages (one says "nothing was saved, try again", the other says "somebody
 * beat you to it" and closes the wizard).
 */
export type ReviewClaimResult = 'ok' | 'already-resolved' | 'check-failed';

export class VisitorCheckinContext {
    scan: Accessor<DocumentScanResult | undefined>; setScan: Setter<DocumentScanResult | undefined>;

    vmGU: Accessor<string>; setVmGU: Setter<string>;
    repeatTier: Accessor<'None'|'Grace'|'Remembered'>; setRepeatTier: Setter<'None'|'Grace'|'Remembered'>;
    repeatPriorVisitGU: Accessor<string>; setRepeatPriorVisitGU: Setter<string>;
    vmStationGU: Accessor<string | undefined>; setVmStationGU: Setter<string | undefined>;
    machineInfo: Accessor<MachineInfo | undefined>; setMachineInfo: Setter<MachineInfo | undefined>;
    vmBridgeStatus: Accessor<VisitorManagementBridgeStatus | undefined>; setVmBridgeStatus: Setter<VisitorManagementBridgeStatus | undefined>;
    isUpdatingVisitor: Accessor<boolean>; setIsUpdatingVisitor: Setter<boolean>;
    visitorStatus: Accessor<VisitorStatus | undefined>; setVisitorStatus: Setter<VisitorStatus | undefined>;
    activeCheckin: Accessor<ActiveCheckinInfo | undefined>; setActiveCheckin: Setter<ActiveCheckinInfo | undefined>;
    entryMode: Accessor<'scan' | 'manual' | 'review' | undefined>; setEntryMode: Setter<'scan' | 'manual' | 'review' | undefined>;
    isVerifying: Accessor<boolean>; setIsVerifying: Setter<boolean>
    verificationStatusMessage: Accessor<string>; setVerificationStatusMessage: Setter<string>;
    verificationStatus: Accessor<VerificationStatus>; setVerificationStatus: Setter<VerificationStatus>;
    offenders: Accessor<SexOffender[]>; setOffenders: Setter<SexOffender[]>;
    blockedMatches: Accessor<BlockedVisitorMatchDto[]>; setBlockedMatches: Setter<BlockedVisitorMatchDto[]>;
    printData: Store<DocumentPrintData>; setPrintData: SetStoreFunction<DocumentPrintData>;
    isPrinting: Accessor<boolean>; setIsPrinting: Setter<boolean>;

    cellPhone: Accessor<string>; setCellPhone: Setter<string>;
    email: Accessor<string>; setEmail: Setter<string>;
    visitorType: Accessor<string>; setVisitorType: Setter<string>;

    /**
     * The visitor type the ROW was hydrated with, captured once in hydrateForReview and never
     * written again. '' outside review mode.
     *
     * visitorType above is EDITABLE — the Print step binds a <Select> straight to it, and an
     * operator is meant to be able to correct a row's type. So visitorType() answers "what type
     * will be saved", NOT "what kind of row is this", and anything that ROUTES must ask this signal
     * instead. Deriving the route from the editable value meant an operator who set Visitor Type =
     * 'Staff' on a screening-flagged VISITOR row and pressed Previous turned it into a staff row:
     * the screening panels unmounted and the staff candidate picker rendered stuck on its loading
     * state forever, because the entry effect's once-latch had already fired and could not reload.
     *
     * No setter is exposed. Immutability is the whole point: a routing decision made at hydration
     * must not be reachable from the UI.
     */
    reviewVisitorType: Accessor<string>;
    private setReviewVisitorType: Setter<string>;

    reason: Accessor<string>; setReason: Setter<string>;
    notes: Accessor<string>; setNotes: Setter<string>;
    meetingWith: Accessor<string>; setMeetingWith: Setter<string>;
    meetingWithName: Accessor<string>; setMeetingWithName: Setter<string>;
    selectedPrinterName: Accessor<string>; setSelectedPrinterName: Setter<string>;
    history: Accessor<CheckInConfigurationHistoryEntry[]>; setHistory: Setter<CheckInConfigurationHistoryEntry[]>;

    documentReader?: DocumentReaderContext;

    // reviewVisitorGU: in review mode the visitor row already exists, so its GU is
    // supplied here and the new-GU path below is short-circuited — uuidv4() is never
    // even evaluated, so no throwaway GU can leak into printData or the API calls.
    constructor(reviewVisitorGU?: string) {
        const initialVmGU = reviewVisitorGU || uuidv4().toUpperCase();

        [this.vmGU, this.setVmGU] = createSignal(initialVmGU);
        [this.repeatTier, this.setRepeatTier] = createSignal<'None'|'Grace'|'Remembered'>('None');
        [this.repeatPriorVisitGU, this.setRepeatPriorVisitGU] = createSignal<string>('');
        [this.vmStationGU, this.setVmStationGU] = createSignal<string | undefined>(
            localStorage.getItem('st-visitor-checkin.station-gu') ?? undefined
        );
        [this.machineInfo, this.setMachineInfo] = createSignal<MachineInfo | undefined>();
        [this.vmBridgeStatus, this.setVmBridgeStatus] = createSignal<VisitorManagementBridgeStatus | undefined>();
        [this.isUpdatingVisitor, this.setIsUpdatingVisitor] = createSignal<boolean>(false);
        [this.visitorStatus, this.setVisitorStatus] = createSignal<VisitorStatus>();
        [this.activeCheckin, this.setActiveCheckin] = createSignal<ActiveCheckinInfo>();
        [this.entryMode, this.setEntryMode] = createSignal<'scan' | 'manual' | 'review' | undefined>(undefined);

        createEffect(() => {
            const gu = this.vmStationGU();
            if (gu) {
                localStorage.setItem('st-visitor-checkin.station-gu', gu);
            }
        });
        [this.scan, this.setScan] = createSignal<DocumentScanResult>();
        [this.isVerifying, this.setIsVerifying] = createSignal<boolean>(false);
        [this.verificationStatusMessage, this.setVerificationStatusMessage] = createSignal<string>('');
        [this.verificationStatus, this.setVerificationStatus] = createSignal<VerificationStatus>('pending');
        [this.cellPhone, this.setCellPhone] = createSignal<string>('');
        [this.email, this.setEmail] = createSignal<string>('');
        [this.visitorType, this.setVisitorType] = createSignal<string>('');
        [this.reviewVisitorType, this.setReviewVisitorType] = createSignal<string>('');
        [this.reason, this.setReason] = createSignal<string>('');
        [this.notes, this.setNotes] = createSignal<string>('');
        [this.meetingWith, this.setMeetingWith] = createSignal<string>('');
        [this.meetingWithName, this.setMeetingWithName] = createSignal<string>('');
        [this.offenders, this.setOffenders] = createSignal<SexOffender[]>([]);
        [this.blockedMatches, this.setBlockedMatches] = createSignal<BlockedVisitorMatchDto[]>([]);
        [this.printData, this.setPrintData] = createStore<DocumentPrintData>({
            VisitorType: '',
            FirstName: '',
            LastName: '',
            CheckInDateTime: new Date().toLocaleString(),
            ImageBase64: '',
            MeetingWith: '',
            Reason: '',
            VisitorBarCode: initialVmGU,
            GUID: initialVmGU,
        });
        [this.isPrinting, this.setIsPrinting] = createSignal<boolean>(false);

        const preferredPrinter = localStorage.getItem('st-visitor-checkin.preferred-printer') || '';
        [this.selectedPrinterName, this.setSelectedPrinterName] = createSignal<string>(preferredPrinter);

        createEffect(() => {
            const s = this.scan();
            if (s) {
                this.setPrintData('FirstName', s.AAMVA.firstName ?? '');
                this.setPrintData('LastName', s.AAMVA.lastName ?? '');
                this.setPrintData('ImageBase64', s.photoBase64 ?? '');
                this.setPrintData('CheckInDateTime', new Date().toLocaleDateString());
            }
        });

        createEffect(() => {
            if (!this.selectedPrinterName()) return;
            localStorage.setItem('st-visitor-checkin.preferred-printer', this.selectedPrinterName());
        });

        [this.history, this.setHistory] = createSignal<CheckInConfigurationHistoryEntry[]>(this.getHistoryEntries());

        createEffect(() => {
            this.persistHistoryEntries(this.history());
        })
    }

    addHistoryEntry(entry: CheckInConfigurationHistoryEntry) {
        const history = this.history();
        const previous = history[0];

        if (previous && previous.visitorType === entry.visitorType
            && previous.reason === entry.reason
            && previous.notes === entry.notes
            && previous.meetingWith === entry.meetingWith) {
            return;
        }

        const maxEntries = 5;
        if (history.length + 1 > maxEntries) {
            history.pop();
        }

        this.setHistory([entry, ...this.history()]);
    }

    loadFromHistoryEntry(entry: CheckInConfigurationHistoryEntry) {
        this.setVisitorType(entry.visitorType);
        this.setReason(entry.reason);
        this.setNotes(entry.notes);
        this.setMeetingWith(entry.meetingWith);
        this.setMeetingWithName(entry.meetingWithName ?? '');
    }

    private getHistoryEntries(): CheckInConfigurationHistoryEntry[] {
        const raw = localStorage.getItem('st-visitor-checkin.history');
        return raw ? JSON.parse(raw) : [];
    }

    private persistHistoryEntries(entries: CheckInConfigurationHistoryEntry[]) {
        localStorage.setItem('st-visitor-checkin.history', JSON.stringify(entries));
    }

    onScan(scan: DocumentScanResult | undefined) {
        this.setScan(scan);
        this.setVisitorStatus(undefined);
        this.setActiveCheckin(undefined);
        this.setVerificationStatus('pending');
        this.setVerificationStatusMessage('');
        this.setOffenders([]);
        this.setBlockedMatches([]);
        this.setRepeatTier('None');
        this.setRepeatPriorVisitGU('');
    }

    /**
     * Staff [Review] entry point. The kiosk already captured this visitor and screening
     * flagged them, so instead of scanning we rebuild the same state a scan would have
     * produced from the pushed payload — keeping the row's existing visitor GU, because
     * no new EPC_VM_VISITOR record is being created here.
     */
    hydrateForReview(payload: ReviewPayload) {
        const aamva = new AAMVAInfo();
        aamva.firstName = payload.firstName ?? '';
        aamva.lastName = payload.lastName ?? '';
        aamva.dateOfBirth = reviewDobToDateTimeString(payload.dateOfBirth);
        aamva.idNumber = payload.idNumber ?? '';

        const scan = new DocumentScanResult();
        scan.AAMVA = aamva;
        if (payload.portraitBase64) scan.photoBase64 = payload.portraitBase64;
        if (payload.idImageBase64) scan.idBase64 = payload.idImageBase64;

        // The GU must be the row's. The constructor is normally handed it directly, but
        // re-assert it here (plus the two printData fields seeded at construction, which
        // are a snapshot rather than reactive) in case the payload arrived after mount.
        this.setVmGU(payload.vmVisitorGU);
        this.setPrintData('GUID', payload.vmVisitorGU);
        this.setPrintData('VisitorBarCode', payload.vmVisitorGU);

        // THE ROW'S OWN TYPE, captured immutably. Written BEFORE setEntryMode below, deliberately:
        // the Verification step's entry effect tracks entryMode() and routes on this value, and a
        // write made outside a batch re-runs that effect synchronously. Setting the type first means
        // the router can never observe entryMode 'review' with the type still ''. (Today
        // hydrateForReview is called from inside an effect, so the writes are batched and the order
        // is moot — this does not depend on that staying true.)
        this.setReviewVisitorType(payload.visitorType ?? '');

        this.setEntryMode('review');

        // onScan resets verification/offender/repeat state exactly like a fresh scan,
        // so it must run before visitorStatus is set (it clears visitorStatus).
        this.onScan(scan);
        this.setVisitorStatus('REVW');

        // All four are set unconditionally with a ?? '' fallback. An absent key means the
        // kiosk captured nothing for it, and that must clear the signal rather than leave a
        // stale value behind — the Print step binds straight to these.
        //
        // visitorType is the EDITABLE copy of the same payload value reviewVisitorType captured
        // above. Both are seeded here; only this one can subsequently change, and only this one is
        // what gets saved. Nothing may route on it — see reviewVisitorType's declaration.
        this.setVisitorType(payload.visitorType ?? '');
        this.setReason(payload.checkInReason ?? '');
        this.setMeetingWith(payload.meetingWith ?? '');
        this.setMeetingWithName(payload.meetingWithName ?? '');
    }

    /**
     * Concurrency pre-check shared by EVERY review-mode write: the resolve buttons on the
     * Verification step and the single CompleteCheckin in the wizard's submit(). Two staff can
     * have the same [Review] row open; without this the second one to act would silently
     * overwrite the first one's decision.
     *
     * Lives on the context rather than in either caller so there is exactly one implementation
     * and one definition of what the three outcomes mean. Returns 'ok' immediately, with no
     * network call at all, outside review mode — an ordinary walk-in is not a REVW row, so the
     * non-review paths behave exactly as they always did.
     */
    async claimReviewRow(): Promise<ReviewClaimResult> {
        if (this.entryMode() !== 'review') return 'ok';

        try {
            return await api.IsAwaitingReview(this.vmGU()) ? 'ok' : 'already-resolved';
        } catch (e) {
            // The row's state is UNKNOWN, which is not the same as "already resolved". Callers
            // must still fail closed (write nothing) but must say so honestly and keep the
            // operator's work on screen.
            console.error(e);
            return 'check-failed';
        }
    }

    async verify(): Promise<boolean> {
        this.setIsVerifying(true);
        this.setVerificationStatus('verifying');

        try {
            this.setVerificationStatusMessage('Checking blocked / No-Fly list...');
            const blocked = await api.VerifyBlockedVisitor(this.scan()!, this.vmStationGU()!, this.vmGU());
            if (blocked.tier === 'Block') {
                this.setVerificationStatus('dangerous');
                this.setVerificationStatusMessage(blocked.message || 'Visitor is on the blocked list.');
                this.setBlockedMatches(blocked.matches);
                return false;
            }
            if (blocked.tier === 'Warn') {
                this.setVerificationStatus('review');
                this.setVerificationStatusMessage('Possible match on the blocked / No-Fly list — staff review required.');
                this.setBlockedMatches(blocked.matches);
                return false;
            }
            // tier === 'None' → continue to sex-offender check

            this.setVerificationStatusMessage('Running Sex Offender Registry Verification...')
            const sexOffenderVerification = await api.VerifySexOffender(this.scan()!);
            if (!sexOffenderVerification.passed) {
                this.setVerificationStatus('dangerous');
                this.setVerificationStatusMessage('Scanned ID failed sex offender verification test.');
                this.setOffenders(sexOffenderVerification.matchingOffenders);
                return false;
            }

            this.setVerificationStatus('verified');
            this.setVerificationStatusMessage('Verification Complete');

            return true;

        } catch(e) {
            console.error(e);
            toast.notify('error', 'An error occurred during the verification process.');
            this.setVerificationStatus('error')
            this.setVerificationStatusMessage('Internal Error Occurred');
            return false;
        } finally {
            this.setIsVerifying(false);
        }
    }

    /**
     * Print the badge and report honestly.
     *
     * Returns true only when the bridge accepted the job. The success toast used to fire
     * unconditionally — the return value of `visitorManagementClient.print` was discarded — which is
     * how a badge that never printed still said "Printed ID card!".
     *
     * Never throws, and a failure here must NEVER be treated as a reason to undo the check-in: by
     * the time this runs the visitor is already admitted (CompleteCheckin has recorded ALLOW) and
     * staff can reprint from the grid.
     */
    async print(): Promise<boolean> {
        if (this.isPrinting())
            return false;

        this.setIsPrinting(true);
        try {
            const request: PrintRequest = {
                Data: this.printData,
                PrinterName: this.selectedPrinterName(),
            }

            this.addHistoryEntry({
                visitorType: this.visitorType(),
                reason: this.reason(),
                notes: this.notes(),
                meetingWith: this.meetingWith(),
                meetingWithName: this.meetingWithName(),
            });

            const result = await visitorManagementClient.print(request);

            if (!result.success) {
                toast.notify('error', result.message
                    ? `Could not print the ID card: ${result.message} The visitor is still checked in — use Reprint on the grid to try again.`
                    : 'Could not print the ID card. The visitor is still checked in — use Reprint on the grid to try again.');
                return false;
            }

            toast.notify('success', 'Printed ID card!')
            return true;
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error ocurred while printing. The visitor is still checked in — use Reprint on the grid to try again.');
            return false;
        } finally {
            this.setIsPrinting(false);
        }
    }
}

export const VisitorCheckinContextJsx = createContext<VisitorCheckinContext>();
export const requireVisitorCheckinContext = () => {
    return requireContext(VisitorCheckinContextJsx);
}